import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  WrenchScrewdriverIcon, 
  BuildingOfficeIcon, 
  ScaleIcon, 
  TruckIcon,
  SparklesIcon,
  HomeModernIcon,
  PaintBrushIcon,
  HomeIcon,
  CameraIcon,
  FireIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

interface AdditionalService {
  id: number;
  title: string;
  description: string;
  icon?: string;
  color: string;
  image?: string;
  order: number;
}

const Services: React.FC = () => {
  // Fixed services that always appear first
  const fixedServiceCategories = [
    {
      title: 'General Services',
      image: '/images/gallery/37.png',
      description: 'Comprehensive construction and plumbing services for all your residential and commercial needs.',
      link: '#general-services'
    },
    {
      title: 'Calibration Services',
      image: '/images/gallery/35.png',
      description: 'Professional tank calibration and measurement services ensuring accuracy and compliance.',
      link: '#calibration-services'
    },
    {
      title: 'Other Services',
      image: '/images/gallery/32.png',
      description: 'Specialized engineering solutions including electrical, CCTV, and security systems.',
      link: '#other-services'
    }
  ];

  // Fixed "What We Do" services
  const fixedWhatWeDo = [
    { name: 'House plumbing', icon: WrenchScrewdriverIcon, color: 'bg-blue-500' },
    { name: 'Calibration services', icon: ScaleIcon, color: 'bg-green-500' },
    { name: 'Fuel station construction', icon: TruckIcon, color: 'bg-orange-500' },
    { name: 'Fabrication of tanks and other designs', icon: BuildingOfficeIcon, color: 'bg-purple-500' },
    { name: 'Painting and Renovation works', icon: PaintBrushIcon, color: 'bg-pink-500' },
    { name: 'Architectural designing and Landscaping', icon: HomeModernIcon, color: 'bg-indigo-500' },
    { name: 'House roofing and systems', icon: HomeIcon, color: 'bg-red-500' },
    { name: 'Tilling, Swimming pool and Fountain designing', icon: SparklesIcon, color: 'bg-cyan-500' },
    { name: 'CCTV cameras, electrical wiring and razor wires', icon: CameraIcon, color: 'bg-yellow-500' },
    { name: 'Biogas systems and Kitchen interiors', icon: FireIcon, color: 'bg-lime-500' },
    { name: 'General Alarms and Automatic gates', icon: ShieldCheckIcon, color: 'bg-teal-500' },
    { name: 'Cleaning and housekeeping services', icon: SparklesIcon, color: 'bg-emerald-500' },
    { name: 'Roof repairs and General contracts', icon: LightBulbIcon, color: 'bg-amber-500' }
  ];

  const [additionalServices, setAdditionalServices] = useState<AdditionalService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch additional services from API
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://plencoplumbingltd.com/api/services');
        
        if (!response.ok) {
          throw new Error('Failed to fetch services');
        }
        
        const result = await response.json();
        
        if (result.success && result.data) {
          setAdditionalServices(result.data);
        }
      } catch (err) {
        console.error('Error fetching services:', err);
        // Don't show error - services are optional
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  // Combine fixed and additional services for "What We Do" section
  const allWhatWeDoServices = [
    ...fixedWhatWeDo,
    ...additionalServices.map(service => ({
      name: service.title,
      icon: WrenchScrewdriverIcon, // Default icon since custom icons are harder to map
      color: service.color || 'bg-primary'
    }))
  ];

  return (
    <div className="bg-white">
      <SEO 
        title="Services - Construction, Plumbing & Tank Calibration | Plenco Uganda"
        description="Comprehensive services including general plumbing, tank calibration, building construction, renovation works, fuel station construction, electrical wiring, roofing, CCTV installation, biogas systems, and more across Uganda and East Africa. Licensed and certified professionals available 24/7."
        keywords="general plumbing services Uganda, tank calibration Kampala, building construction East Africa, renovation works Uganda, fuel station construction, electrical wiring Kampala, roofing services Uganda, CCTV installation, biogas systems, landscaping Uganda, house plumbing, commercial plumbing Kampala"
        canonicalUrl="https://www.plencoplumbingltd.com/services"
        type="website"
      />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-700 text-white pt-32 sm:pt-36 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block bg-white/10 px-4 py-1 rounded-full text-sm font-semibold mb-4">OUR SERVICES</div>
          <h1 className="text-4xl sm:text-5xl font-black mb-4">
            <span className="text-white">Our</span> <span className="text-primary-200">Services</span>
          </h1>
          <p className="text-xl text-primary-100 max-w-3xl mx-auto">
            Comprehensive construction, plumbing, and engineering solutions tailored to your needs
          </p>
        </div>
      </section>

      {/* Service Categories */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {fixedServiceCategories.map((category, index) => (
              <a
                key={index}
                href={category.link}
                className="group bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border-t-4 border-primary"
              >
                <div className="h-56 overflow-hidden">
                  <img 
                    src={category.image} 
                    alt={category.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-black text-gray-900 mb-3 group-hover:text-primary transition-colors">
                    {category.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {category.description}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="bg-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 mb-6">
            <div>
              <h2 className="text-3xl font-black mb-2">
                <span className="text-primary">What</span> We Do
              </h2>
              <p className="text-gray-600 text-sm font-semibold mb-4">Trusted Service Provider Since 2013</p>
              <p className="text-gray-600 text-base mb-6 leading-relaxed">
                Our plumbing contractors provide courteous, friendly, affordable, and effective residential plumbing services.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {allWhatWeDoServices.map((service, index) => {
                  const colors = [
                    'bg-blue-50',
                    'bg-green-50',
                    'bg-orange-50',
                    'bg-purple-50',
                    'bg-pink-50',
                    'bg-indigo-50',
                    'bg-cyan-50',
                    'bg-amber-50',
                    'bg-emerald-50',
                    'bg-rose-50',
                    'bg-sky-50',
                    'bg-teal-50',
                    'bg-lime-50'
                  ];
                  return (
                    <div key={index} className={`flex items-center space-x-2 ${colors[index % colors.length]} p-3 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all`}>
                      <div className="bg-primary rounded-lg p-1.5">
                        <service.icon className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{service.name}</span>
                    </div>
                  );
                })}
              </div>
              {additionalServices.length > 0 && (
                <p className="text-xs text-gray-500 mt-4">
                  + {additionalServices.length} additional service(s) available
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Service Cards with Images */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-black mb-4">
              <span className="text-primary">Featured</span> <span className="text-gray-600">Services</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Explore our specialized service offerings delivering quality results across East Africa
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Construction & Renovation */}
            <div className="group bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-blue-500">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="/images/gallery/IMG-20241003-WA0002.jpg" 
                  alt="Construction & Renovation"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-2xl font-black text-white mb-2">Construction & Renovation</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-600 leading-relaxed mb-4">
                  Professional building construction and renovation services transforming properties with modern designs, 
                  quality materials, and expert craftsmanship for residential and commercial projects.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    <span className="text-sm">New Construction</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    <span className="text-sm">Building Renovation</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    <span className="text-sm">Structural Works</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Tank Fabrication & Fuel Stations */}
            <div className="group bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-green-500">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="/images/gallery/IMG_20230914_133609_489-1.jpg" 
                  alt="Tank Fabrication"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-2xl font-black text-white mb-2">Tank Fabrication & Fuel Stations</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-600 leading-relaxed mb-4">
                  Expert tank fabrication, calibration, and fuel station construction services. Custom-designed storage 
                  solutions meeting industry standards with precise measurements and compliance certification.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                    <span className="text-sm">Tank Calibration</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                    <span className="text-sm">Custom Fabrication</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                    <span className="text-sm">Fuel Station Setup</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Electrical & Security Systems */}
            <div className="group bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-orange-500">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="/images/gallery/Fire-Safety.jpg" 
                  alt="Electrical & Security"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-2xl font-black text-white mb-2">Electrical & Security Systems</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-600 leading-relaxed mb-4">
                  Complete electrical installation, CCTV camera systems, security alarms, and fire safety solutions. 
                  Advanced technology protecting your property with reliable 24/7 monitoring capabilities.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                    <span className="text-sm">CCTV Installation</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                    <span className="text-sm">Electrical Wiring</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                    <span className="text-sm">Fire Safety Systems</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-black mb-4">
              <span className="text-primary">Get In</span> <span className="text-gray-600">Touch</span>
            </h2>
            <p className="text-gray-600 text-lg">
              Contact us today for professional construction and plumbing services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Head Office */}
            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-lg mb-4">
                <MapPinIcon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-black text-gray-900 mb-2">Head Office</h3>
              <p className="text-gray-600 text-sm">
                KIU World Saloon Building<br />
                Basement Floor, Room B05<br />
                Kansanga, Ggaba Rd<br />
                P.O Box 148303<br />
                Kampala, Uganda
              </p>
            </div>

            {/* Phone */}
            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mb-4">
                <PhoneIcon className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-black text-gray-900 mb-2">Contact Us</h3>
              <a href="tel:+256751503292" className="text-primary hover:underline text-sm font-semibold block">
                +256 751 503292
              </a>
            </div>

            {/* Email */}
            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mb-4">
                <EnvelopeIcon className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-black text-gray-900 mb-2">Email Us</h3>
              <a href="mailto:info@plencoplumbingltd.com" className="text-primary hover:underline text-sm block mb-1">
                info@plencoplumbingltd.com
              </a>
              <a href="mailto:plencoplumbing@gmail.com" className="text-primary hover:underline text-sm block">
                plencoplumbing@gmail.com
              </a>
            </div>

            {/* Working Hours */}
            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-center w-12 h-12 bg-orange-100 rounded-lg mb-4">
                <ClockIcon className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-black text-gray-900 mb-2">Working Time</h3>
              <p className="text-gray-600 text-sm">
                Mon - Fri<br />
                8:00AM - 6:00PM<br />
                <span className="mt-1 block">Sat: 9:00AM - 3:00PM</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Service Banner */}
      <section className="bg-gradient-to-r from-primary to-primary-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-black mb-4">Emergency Plumbing Service</h2>
            <p className="text-xl text-primary-100 mb-6">24 Hours 7 Days a Week</p>
            <p className="text-lg text-primary-50 mb-8">Quality Plumbing Services from a Team of Professionals</p>
            <Link
              to="/contact"
              className="inline-block bg-white text-primary px-8 py-4 rounded-lg font-black hover:bg-primary-50 transition-colors shadow-lg"
            >
              Request Service Now
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
